import { Moment } from 'moment';
import { toMoment } from './date';
import { round } from './math';
import normalize from './normalizeNumber';

export const NEGATIVE_WORKING_TIME_ERROR = 'This ETD would require setting negative working time';
export const NEGATIVE_IDLE_TIME_ERROR = 'This ETD would require setting negative idle time';

export type PortCallDays = {
	estimatedIdleDays?: number | null;
	estimatedLoadingDays?: number | null;
	estimatedDischargingDays?: number | null;
	estimatedTurningDays?: number | null;
};

type AdjustedPortCallDays = {
	estimatedIdleDays: number;
	estimatedLoadingDays: number;
	estimatedDischargingDays: number;
};

export const getEstimatedTimeInPort = (days: PortCallDays) => (
	normalize(days.estimatedIdleDays) +
	normalize(days.estimatedLoadingDays) +
	normalize(days.estimatedDischargingDays) +
	normalize(days.estimatedTurningDays)
);

const isNegative = (days: number) => round(days, 6) < 0;

const removeFloatNoise = (days: number) => round(days, 10);

export const getPortDaysForTargetEtd = ({
	arrivalDate,
	targetEtd,
	days,
	hasLoading,
	hasDischarging,
}: {
	arrivalDate: Moment | string;
	targetEtd: Moment | string;
	days: PortCallDays;
	hasLoading: boolean;
	hasDischarging: boolean;
}): { days: AdjustedPortCallDays; error: null } | { days: null; error: string } => {
	const currentEtd = toMoment(arrivalDate)
		.add(getEstimatedTimeInPort(days) * 24, 'hours')
		.startOf('minute');
	const extraDays = toMoment(targetEtd).startOf('minute').diff(currentEtd, 'minutes') / (24 * 60);

	const adjusted: AdjustedPortCallDays = {
		estimatedIdleDays: normalize(days.estimatedIdleDays),
		estimatedLoadingDays: normalize(days.estimatedLoadingDays),
		estimatedDischargingDays: normalize(days.estimatedDischargingDays),
	};

	if (!hasLoading && !hasDischarging) {
		adjusted.estimatedIdleDays = removeFloatNoise(adjusted.estimatedIdleDays + extraDays);

		if (isNegative(adjusted.estimatedIdleDays)) {
			return { days: null, error: NEGATIVE_IDLE_TIME_ERROR };
		}

		return { days: adjusted, error: null };
	}

	if (hasLoading && hasDischarging) {
		adjusted.estimatedLoadingDays = removeFloatNoise(adjusted.estimatedLoadingDays + extraDays / 2);
		adjusted.estimatedDischargingDays = removeFloatNoise(adjusted.estimatedDischargingDays + extraDays / 2);
	} else if (hasLoading) {
		adjusted.estimatedLoadingDays = removeFloatNoise(adjusted.estimatedLoadingDays + extraDays);
	} else {
		adjusted.estimatedDischargingDays = removeFloatNoise(adjusted.estimatedDischargingDays + extraDays);
	}

	if (isNegative(adjusted.estimatedLoadingDays) || isNegative(adjusted.estimatedDischargingDays)) {
		return { days: null, error: NEGATIVE_WORKING_TIME_ERROR };
	}

	return { days: adjusted, error: null };
};
